import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useAnalytics from '../hooks/useAnalytics';
import './ChatBot.css';

const ChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [visitorCount, setVisitorCount] = useState(0);
  const [userLocation, setUserLocation] = useState('');
  const [freeRequestsLeft, setFreeRequestsLeft] = useState(5);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userTestimonial, setUserTestimonial] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Initialize analytics
  const { trackEvent } = useAnalytics();

  // Track page view on component mount
  useEffect(() => {
    trackEvent('page_view', 'navigation', 'ChatBot Page Loaded');
  }, [trackEvent]);

  // Get user ID from localStorage or generate new one
  const getUserId = () => {
    let userId = localStorage.getItem('userId');
    if (!userId) {
      userId = 'user_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('userId', userId);
      
      // Track new user
      trackEvent('new_user', 'user', 'New User Created', 1);
    }
    return userId;
  };

  const userId = getUserId();

  // Initialize user data
  useEffect(() => {
    // Get or initialize request count
    const storedRequests = localStorage.getItem('freeRequests');
    if (storedRequests) {
      setFreeRequestsLeft(parseInt(storedRequests));
    } else {
      localStorage.setItem('freeRequests', '5');
      setFreeRequestsLeft(5);
    }

    // Check if user is premium
    const premiumStatus = localStorage.getItem('isPremium');
    setIsPremium(premiumStatus === 'true');

    // Check if we need to show email modal
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) {
      // Show email modal after a short delay
      setTimeout(() => {
        setShowEmailModal(true);
      }, 2000);
    } else {
      setUserEmail(userEmail);
    }

    // Initialize visitor count
    const storedCount = localStorage.getItem('visitorCount');
    if (storedCount) {
      setVisitorCount(parseInt(storedCount));
    } else {
      const initialCount = Math.floor(Math.random() * 150) + 50;
      setVisitorCount(initialCount);
      localStorage.setItem('visitorCount', initialCount.toString());
    }

    // Get user location
    fetchUserLocation();
    
    // Increment visitor count
    incrementVisitorCount();
  }, []);

  // Fetch user's location based on IP
  const fetchUserLocation = async () => {
    try {
      const response = await axios.get('https://ipapi.co/json/');
      const { city, region, country_name } = response.data;
      const location = `${city}, ${region}, ${country_name}`;
      setUserLocation(location);
      localStorage.setItem('userLocation', location);
    } catch (error) {
      console.error('Error fetching location:', error);
      setUserLocation('Unknown Location');
    }
  };

  // Increment visitor count
  const incrementVisitorCount = () => {
    const newCount = visitorCount + 1;
    setVisitorCount(newCount);
    localStorage.setItem('visitorCount', newCount.toString());
  };

  // Check if user can make requests
  const canMakeRequest = () => {
    return isPremium || freeRequestsLeft > 0;
  };

  // Update request count
  const updateRequestCount = () => {
    if (isPremium) return; // Unlimited requests for premium
    
    const newCount = freeRequestsLeft - 1;
    setFreeRequestsLeft(newCount);
    localStorage.setItem('freeRequests', newCount.toString());
    
    // Show testimonial modal when requests are exhausted
    if (newCount === 0) {
      setTimeout(() => {
        setShowTestimonialModal(true);
      }, 1500);
    }
  };

  // Handle email submission
  const handleEmailSubmit = () => {
    if (userEmail && userEmail.includes('@')) {
      localStorage.setItem('userEmail', userEmail);
      
      // Track email submission
      trackEvent('email_submitted', 'user', 'Email Collected', 1);
      
      sendEmailNotification('welcome', {});
      setShowEmailModal(false);
      
      setMessages(prev => [...prev, { 
        sender: 'ai', 
        text: "Thanks for providing your email! You now have 5 free requests this month.",
        isMarkdown: false
      }]);
    }
  };

  // Submit testimonial to MySQL database
  const submitTestimonial = async () => {
    try {
      // Send testimonial to your server
      const response = await fetch('https://35seo-checklist-ai-assistant.linkwatch.in/submit_testimonial.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          email: userEmail,
          testimonial: userTestimonial,
          rating: 5
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Track testimonial submission
        trackEvent('testimonial_submitted', 'feedback', 'Testimonial Provided', 1);
        
        // Send thank you email
        sendEmailNotification('testimonial-thanks', {});
        
        setShowTestimonialModal(false);
        setUserTestimonial('');
        
        // Show thank you message
        setMessages(prev => [...prev, { 
          sender: 'ai', 
          text: "Thank you for your feedback! We appreciate your support.",
          isMarkdown: false
        }]);
      } else {
        throw new Error(data.message || 'Failed to submit testimonial');
      }
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      // Show error message
      setMessages(prev => [...prev, { 
        sender: 'ai', 
        text: "Sorry, we encountered an error while submitting your feedback. Please try again later.",
        isMarkdown: false
      }]);
    }
  };

  // Send email notification
  const sendEmailNotification = async (type, data) => {
    try {
      // In a real app, you would call your email API here
      console.log('Sending email notification:', type, data);
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

  // Handle sending messages with tracking
  const handleSend = async () => {
    if (!input.trim() || !canMakeRequest()) return;

    // Track message sent
    trackEvent('message_sent', 'chat', 'User Message', 1);

    setMessages(prev => [...prev, { sender: 'user', text: input }]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post(
        process.env.REACT_APP_MAKE_WEBHOOK_URL,
        { 
          message: input,
          userId: localStorage.getItem('userId')
        },
        { 
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 30000
        }
      );

      let aiResponse = "";
      
      if (response.data && response.data.reply) {
        aiResponse = response.data.reply;
      } else if (response.data && typeof response.data === 'string') {
        aiResponse = response.data;
      } else {
        aiResponse = "Received an unexpected response format.";
      }

      // Update request count after successful response
      updateRequestCount();

      // Track successful response
      trackEvent('ai_response', 'chat', 'AI Response Received', 1);

      setMessages(prev => [...prev, { 
        sender: 'ai', 
        text: aiResponse,
        isMarkdown: true
      }]);

    } catch (error) {
      console.error('API Error:', error);
      
      // Track error
      trackEvent('api_error', 'chat', 'API Error', 1);
      
      setMessages(prev => [...prev, { 
        sender: 'ai', 
        text: "Sorry, I'm experiencing technical difficulties. Please try again later.",
        isMarkdown: false
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  useEffect(scrollToBottom, [messages]);

  // Custom renderers for markdown components
  const MarkdownComponents = {
    p: ({node, ...props}) => <p className="markdown-p" {...props} />,
    h1: ({node, ...props}) => <h1 className="markdown-h1" {...props} />,
    h2: ({node, ...props}) => <h2 className="markdown-h2" {...props} />,
    h3: ({node, ...props}) => <h3 className="markdown-h3" {...props} />,
    h4: ({node, ...props}) => <h4 className="markdown-h4" {...props} />,
    ul: ({node, ...props}) => <ul className="markdown-ul" {...props} />,
    ol: ({node, ...props}) => <ol className="markdown-ol" {...props} />,
    li: ({node, ...props}) => <li className="markdown-li" {...props} />,
    table: ({node, ...props}) => <table className="markdown-table" {...props} />,
    th: ({node, ...props}) => <th className="markdown-th" {...props} />,
    td: ({node, ...props}) => <td className="markdown-td" {...props} />,
    code: ({node, ...props}) => <code className="markdown-code" {...props} />,
    blockquote: ({node, ...props}) => <blockquote className="markdown-blockquote" {...props} />,
  };

  return (
    <div className="chatbot-container">
      {/* Free requests counter */}
      {!isPremium && (
        <div className="requests-counter">
          <div className="counter-badge">
            <span className="counter-icon">✨</span>
            <span className="counter-number">{freeRequestsLeft}</span>
            <span className="counter-label">Free Requests Left</span>
          </div>
        </div>
      )}

      {/* Premium badge */}
      {isPremium && (
        <div className="premium-badge">
          <span className="premium-icon">⭐</span>
          <span className="premium-text">Premium Member</span>
        </div>
      )}

      {/* Email Collection Modal */}
      {showEmailModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Welcome to SEO Checklist Assistant! 👋</h3>
            <p>We'd like to send you SEO tips and updates. What's your email address?</p>
            <input
              type="email"
              placeholder="Enter your email address"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              className="email-input"
            />
            <div className="modal-buttons">
              <button 
                className="primary-btn" 
                onClick={handleEmailSubmit}
                disabled={!userEmail.includes('@')}
              >
                Continue
              </button>
              <button 
                className="secondary-btn" 
                onClick={() => setShowEmailModal(false)}
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Testimonial Modal */}
      {showTestimonialModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Share Your Experience! 💬</h3>
            <p>You've used all 5 free requests this month. We'd love to hear your feedback!</p>
            <textarea
              placeholder="How has our SEO Checklist Assistant helped you?"
              value={userTestimonial}
              onChange={(e) => setUserTestimonial(e.target.value)}
              className="testimonial-input"
              rows="4"
            />
            <div className="modal-buttons">
              <button 
                className="primary-btn" 
                onClick={submitTestimonial}
                disabled={userTestimonial.length < 10}
              >
                Submit Feedback
              </button>
              <button 
                className="secondary-btn" 
                onClick={() => setShowTestimonialModal(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      <header>
        <h1>SEO Checklist AI Assistant 🤖</h1>
        <p>Get expert guidance on the 35-point SEO checklist</p>
        
        <div className="visitor-counter">
          <div className="counter-badge">
            <span className="counter-icon">👁️</span>
            <span className="counter-number">{visitorCount.toLocaleString()}</span>
            <span className="counter-label">Live Visitors</span>
          </div>
          {userLocation && (
            <div className="location-badge">
              <span className="location-icon">📍</span>
              <span className="location-text">{userLocation}</span>
            </div>
          )}
        </div>
      </header>

      <div className="messages-container">
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.sender}`}>
            <div className="message-bubble">
              <div className="message-content">
                {msg.sender === 'ai' && <span className="ai-icon">🤖</span>}
                {msg.isMarkdown ? (
                  <div className="markdown-response">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={MarkdownComponents}
                    >
                      {msg.text}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-response">{msg.text}</div>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message ai">
            <div className="message-bubble">
              <div className="message-content">
                <span className="ai-icon">🤖</span>
                <div className="typing-indicator">
                  <span>Thinking</span>
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="input-container">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={
            !isPremium && freeRequestsLeft === 0 
              ? "You've used all 5 free requests this month" 
              : "Ask about SEO checklist (e.g., 'Show me technical SEO points')"
          }
          disabled={isLoading || (!isPremium && freeRequestsLeft === 0)}
        />
        <button 
          onClick={handleSend} 
          disabled={
            isLoading || 
            !input.trim() || 
            (!isPremium && freeRequestsLeft === 0)
          }
        >
          {isLoading ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default ChatBot;